'use client'

import { useState, useMemo } from 'react'

import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, rectSwappingStrategy } from '@dnd-kit/sortable'
import _ from 'lodash'
import { LoaderCircle } from 'lucide-react'

import { LevelFooter } from '../../Footer'
import { HUD } from '../../HUD'
import { LevelToolbar } from '../../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { ChallengeStep, useFlow } from '@/shared/lib/steps-flow'
import { Button } from '@/shared/ui/Button'


import { JigsawPiece } from './Piece'

interface GameJigsawProps {
  step: ChallengeStep
  challengeId: number
}

interface JigsawPiece {
  id: number
  position: number
  src: string
}

export function GameJigsaw({ challengeId }: GameJigsawProps) {
  const { continueDisabled, goNext, enableContinue } = useFlow()
  const { level, isSubmitting, onChallengeAnswer } = useLevelContext()

  const challenge = level?.active_challenges.find(c => c.id === challengeId)
  const { meta } = challenge
  const imageUrl: string = _.get(meta, 'image.file.url', '')
  const difficulty = _.get(meta, 'difficulty', 'hard')
  const [pieces, setPieces] = useState<JigsawPiece[]>([])

  let numRows = 2
  let numCols = 2
  if (difficulty === 'medium') {
    numRows = 3
    numCols = 3
  } else if (difficulty === 'hard') {
    numRows = 4
    numCols = 3
  }

  const handleComplete = async () => {
    if (!challenge) return

    try {
      const { ok } = await onChallengeAnswer({
        uid: challenge.uid,
        answer: `Montou o puzzle de dificuldade ${difficulty}`,
        skipValidation: true
      })
      if (ok) enableContinue()
    } catch (error) {
      console.error('Error completing jigsaw game:', error)
    }
  }

  useMemo(() => {
    const numPieces = numRows * numCols
    const organizedPieces = Array.from({ length: numPieces }, (_, index) => ({
      position: index,
      id: index + 1
    }))

    try {
      const image = new window.Image()
      image.crossOrigin = 'Anonymous'
      image.src = imageUrl + '?not-from-cache-please'
      image.onload = () => {
        const pieceWidth = image.width / numCols
        const pieceHeight = image.height / numRows

        setPieces(
          _.shuffle(
            organizedPieces.map((piece, index) => {
              const canvas = document.createElement('canvas')
              const context = canvas.getContext('2d')

              const col = index % numCols
              const row = Math.floor(index / numCols)

              const x = col * pieceWidth
              const y = row * pieceHeight

              canvas.width = pieceWidth
              canvas.height = pieceHeight
              context?.drawImage(
                image,
                x,
                y,
                pieceWidth,
                pieceHeight,
                0,
                0,
                pieceWidth,
                pieceHeight
              )

              return { ...piece, src: canvas.toDataURL() }
            })
          )
        )
      }
    } catch (error) {
      handleComplete()
    }
     
  }, [imageUrl, numRows, numCols])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      setPieces(pieces => {
        const activeIndex = _.findIndex(pieces, piece => active.id === piece.id)
        const overIndex = _.findIndex(pieces, piece => over?.id === piece.id)

        const updatedPieces = _.cloneDeep(pieces)
        ;[updatedPieces[activeIndex], updatedPieces[overIndex]] = [
          updatedPieces[overIndex],
          updatedPieces[activeIndex]
        ]

        const positions = _.map(updatedPieces, 'position')
        const expectedPositions = _.range(updatedPieces.length)
        const isEqualPositions = _.isEqual(expectedPositions, positions)

        if (isEqualPositions) {
          handleComplete()
        }
        return updatedPieces
      })
    }
  }

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>
      <HUD.Body className="flex flex-col gap-4">
        <HUD.Description challenge={challenge} />

        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={pieces} strategy={rectSwappingStrategy}>
            <div
              className={`grid max-w-[500px] mx-auto w-full`}
              style={{
                gap: '1px',
                gridTemplateColumns: `repeat(${numCols}, 1fr)`,
                gridTemplateRows: `repeat(${numRows}, 1fr)`,
                touchAction: 'none'
              }}
            >
              {pieces.map(piece => (
                <JigsawPiece
                  id={piece.id}
                  key={piece.id}
                  imageSrc={piece.src}
                  position={piece.position}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </HUD.Body>

      <LevelFooter>
        <Button
          type="button"
          onClick={goNext}
          className="w-full font-bold btn btn-info"
          disabled={continueDisabled || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <LoaderCircle className="size-5 animate-spin" />
              <span>Carregando</span>
            </>
          ) : (
            <span>Continuar</span>
          )}
        </Button>
      </LevelFooter>
    </HUD.Card>
  )
}
